import {
  TypeCartProductTranslationsFields,
  TypeProduct,
  TypeProductFields,
  TypeProductPage,
  TypeProductPageFields,
  TypeVariantImageSet,
} from '@/core/generated-types';
import { stringToRichTextDocument } from '@/utils/richTextHelpers';
import {
  CartLineEdge,
  CartLineNode,
  Cart,
  CartLineUpdateInput,
} from '../shopify';
import { KeyedMutator } from 'swr';
import { convertRubyHashToJson } from '@/hooks/usePlayGeniusCheckout/utils/rubyHashToJson';
import { getCartEntries } from '@/lib/contentful';
import { shopifyProductField } from '@/core/contentfulConfig';
import { base64Encode } from '@/utils/helpers';
import buildShopSubdomainUrl from '@/utils/shopSubdomain';
import { FeatureFlagValues } from '@/core/featureFlags';
import {
  convertPublicPropertiesToPrivateProperties,
  shouldConvertCartItemsToHidden,
} from '@/utils/checkoutDecision';
import {
  CONTENT_TYPE_PRODUCT,
  CONTENT_TYPE_PRODUCT_PAGE,
} from '@/core/constants';
import { SerializedCartItem } from '@/hooks/usePlayGeniusCheckout/utils/cartHelpers';
import { VariantOption } from '@/components/cart/VariantDropdownSelector/VariantDropdownSelector';
import { PRODUCT_PAGE_SELECT_FIELDS, PRODUCT_SELECT_FIELDS } from './constants';
import { formatCentsToDollars } from '@/components/subflow-v3/components/utils';

/**
 * Extended cart product translations that includes additional product-level fields
 */
export type ExtendedCartProductTranslations =
  TypeCartProductTranslationsFields & {
    backOrderMessage?: string;
    outOfStockMessage?: string;
  };

/**
 * Calculates subtotal for a cart line item
 */
export const calculateSubtotal = (
  priceAmount: string | number,
  quantity: number,
) => {
  return parseFloat(String(priceAmount)) * quantity;
};

/**
 * Updates a cart line with new quantity and recalculates subtotal
 */
export const updateCartLineQuantity = (
  edge: CartLineEdge,
  newQuantity: number,
) => ({
  ...edge,
  node: {
    ...edge.node,
    quantity: newQuantity,
    cost: {
      ...edge.node.cost,
      subtotalAmount: {
        ...edge.node.cost.subtotalAmount,
        amount: calculateSubtotal(
          edge.node.merchandise.price?.amount || '0',
          newQuantity,
        ),
      },
    },
  },
});

/**
 * Calculates total quantity from cart line edges
 */
export const calculateTotalQuantity = (edges: CartLineEdge[]) =>
  edges.reduce((total, edge) => total + edge.node.quantity, 0);

/**
 * Creates a Map from attributes array for O(1) lookups
 * @param attributes - Array of attribute objects
 * @returns Map with key-value pairs
 */
export function createAttributesMap(
  attributes: Array<{ key: string; value: string }>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const attr of attributes) {
    map.set(attr.key, attr.value);
  }
  return map;
}

/**
 * Creates a getProperty function that checks both regular and underscore-prefixed keys
 * @param attributesMap - Map of attributes
 * @returns Function that returns the value for a key (checking both key and _key)
 */
export function createGetPropertyFunction(
  attributesMap: Map<string, string>,
): (key: string) => string | undefined {
  return (key: string): string | undefined => {
    return attributesMap.get(key) || attributesMap.get(`_${key}`);
  };
}

/**
 * Subscription details extracted from subscription payload
 */
export interface SubscriptionDetails {
  name: string;
  type: 'paygo' | 'puf';
  anchor: string;
  size: number;
}

/**
 * Extracts subscription details from a cart line node
 * @param node - The cart line node to extract subscription details from
 * @returns SubscriptionDetails object or undefined if not a subscription
 */
export function extractSubscriptionDetails(
  node: CartLineNode,
): SubscriptionDetails | undefined {
  const attributes = node.attributes || [];
  const productHandle = node.merchandise.product?.handle;

  // Only process subscription-1 products
  if (productHandle !== 'subscription-1') {
    return undefined;
  }

  const attributesMap = createAttributesMap(attributes);
  const getProperty = createGetPropertyFunction(attributesMap);
  const subscriptionsPayload = getProperty('subscriptionsPayload');

  if (!subscriptionsPayload) {
    return undefined;
  }

  const payload = parseRubyHashToJson<{
    bundle?: { name?: string; size?: number };
    starting_kit_anchor?: string;
  }>(subscriptionsPayload, 'subscriptionsPayload for details');

  if (!payload) {
    return undefined;
  }

  const bundleName = payload.bundle?.name || '';
  const subscriptionType = /prepay/i.test(bundleName) ? 'puf' : 'paygo';

  return {
    name: bundleName,
    type: subscriptionType,
    anchor: payload.starting_kit_anchor || '',
    size: payload.bundle?.size || 1,
  };
}

export interface DisplayPrice {
  price: string;
  currency: string;
  savings?: string;
  subtotal?: string;
}

/**
 * Calculate the display price for a cart item based on complex business logic
 * This replicates the liquid template price logic
 */
export function calculateDisplayPrice(node: CartLineNode): DisplayPrice {
  const attributes = node.attributes || [];
  const productHandle = node.merchandise.product?.handle;
  const currencyCode = node.merchandise.price?.currencyCode || 'USD';

  // Create attributes map for O(1) lookups
  const attributesMap = createAttributesMap(attributes);

  // Extract properties from attributes
  const getProperty = createGetPropertyFunction(attributesMap);

  // Check item types
  const isBookBundle = getProperty('is_book_bundle') === 'true';
  const priceSource = getProperty('price_source');
  const displayPrice = getProperty('display_price');
  const price = getProperty('price');

  // Subscription bundle handles
  const playgeniusPlayKitBundle = 'subscription-1';
  const playgeniusGiftBundle = 'subscription-gifting';

  // Handle subscription payload for Play Kit Bundle
  if (productHandle === playgeniusPlayKitBundle) {
    const subscriptionsPayload = getProperty('subscriptionsPayload');

    if (subscriptionsPayload) {
      const payload = parseRubyHashToJson<{
        bundleDiscountedPrice?: number;
        bundle?: { savings?: number };
      }>(subscriptionsPayload, 'subscriptionsPayload');

      if (payload) {
        const bundleDiscountedPrice = payload.bundleDiscountedPrice;

        if (
          bundleDiscountedPrice !== undefined &&
          bundleDiscountedPrice !== null
        ) {
          return {
            price: String(formatCentsToDollars(bundleDiscountedPrice)),
            currency: currencyCode,
            savings:
              payload.bundle?.savings !== undefined &&
              payload.bundle?.savings !== null
                ? String(formatCentsToDollars(payload.bundle.savings))
                : undefined,
          };
        }
      }
    }
  }

  // Handle book bundles
  if (isBookBundle && price) {
    return {
      subtotal: String(formatCentsToDollars(Number(price))),
      price: Number(node.merchandise.price?.amount).toFixed(2),
      currency: currencyCode,
    };
  }

  // Handle gift bundles
  if (productHandle === playgeniusGiftBundle) {
    const gifts = getProperty('gifts');

    if (gifts) {
      const giftsData = parseRubyHashToJson<{
        bundle?: { price?: string | number };
      }>(gifts, 'gifts data');

      if (giftsData) {
        const bundlePrice = giftsData.bundle?.price;
        if (bundlePrice) {
          return {
            price: String(formatCentsToDollars(Number(bundlePrice))),
            currency: currencyCode,
          };
        }
      }
    }
  }

  // Handle items with price_source and display_price
  if (priceSource && displayPrice) {
    return {
      price: String(formatCentsToDollars(Number(displayPrice))),
      currency: currencyCode,
    };
  }

  // Default: use individual item price, fallback to subtotal if individual price is missing
  const individualPrice = node.merchandise.price?.amount;
  const fallbackPrice =
    individualPrice || String(node.cost.subtotalAmount.amount);

  return {
    price: String(fallbackPrice),
    currency:
      node.merchandise.price?.currencyCode ||
      node.cost.subtotalAmount.currencyCode,
  };
}

/**
 * Checks if a node is a subscription addon using comprehensive detection logic
 * This handles all the various ways an addon can be linked to a subscription
 * @param potentialAddon - The node to check if it's an addon
 * @param subscriptionNode - The subscription node
 * @returns true if the node is an addon for the subscription
 */
export function isSubscriptionAddon(
  potentialAddon: CartLineNode,
  subscriptionNode: CartLineNode,
): boolean {
  if (potentialAddon.id === subscriptionNode.id) return false; // Skip self

  const addonAttributes = potentialAddon.attributes || [];
  const addonAttributesMap = createAttributesMap(addonAttributes);
  const getAddonProperty = createGetPropertyFunction(addonAttributesMap);

  // Check all the various ways an addon can be linked to a subscription
  const addonType = getAddonProperty('type');
  const productTitle = potentialAddon.merchandise.product?.title;
  const hasExplicitParent = getAddonProperty('parent_product_id');
  const isBookBundle =
    getAddonProperty('is_book_bundle') === 'true' ||
    getAddonProperty('_is_book_bundle') === 'true';
  const parentProductId = getAddonProperty('parent_product_id');
  const linkedProductHandle = getAddonProperty('linked_product_handle');
  const subscriptionVariantId = getAddonProperty('subscription_variant_id');
  const productHandle = potentialAddon.merchandise.product?.handle;

  return (
    addonType === 'Subscription Add-On' ||
    (productTitle?.includes('Subscription Add-on') && !hasExplicitParent) ||
    isBookBundle ||
    (parentProductId && parentProductId === subscriptionNode.merchandise.id) ||
    linkedProductHandle === 'subscription-1' ||
    (subscriptionVariantId &&
      subscriptionVariantId === subscriptionNode.merchandise.id) ||
    productHandle?.includes('book-bundle-') ||
    false
  );
}

/**
 * Determines if a potential addon node is actually an addon for the given parent node
 */
export function isAddonForParent(
  potentialAddon: CartLineNode,
  parentNode: CartLineNode,
  _allEdges: { node: CartLineNode }[],
): boolean {
  if (potentialAddon.id === parentNode.id) return false; // Skip self

  const addonAttributes = potentialAddon.attributes || [];
  const addonTitle = potentialAddon.merchandise.product?.title || '';
  const parentAttributes = parentNode.attributes || [];

  // Create attributes maps for O(1) lookups
  const addonAttributesMap = createAttributesMap(addonAttributes);
  const parentAttributesMap = createAttributesMap(parentAttributes);

  // Create getProperty functions for centralized attribute lookup
  const getAddonProperty = createGetPropertyFunction(addonAttributesMap);
  const getParentProperty = createGetPropertyFunction(parentAttributesMap);

  // Priority 1: Check for explicit parent_product_id attribute (highest priority)
  const parentProductId = getAddonProperty('parent_product_id');
  if (parentProductId) {
    // If parent_product_id exists, it must match exactly or return false
    return parentProductId === parentNode.merchandise.id;
  }

  // Priority 2: Only use legacy logic if no parent_product_id exists
  const isCoursePack = getParentProperty('is_course_pack') === 'true';
  const isSubscription =
    getParentProperty('is_playgenius_subscription') === 'true';

  if (!isCoursePack && !isSubscription) return false;

  if (isCoursePack) {
    // For course packs, look for book bundles with linked_product_handle matching parent handle
    const linkedProductHandle = getAddonProperty('linked_product_handle');
    const isBookBundle =
      addonTitle.includes('Book Bundle') ||
      potentialAddon.merchandise.product?.handle?.includes('book-bundle');

    return (
      !!linkedProductHandle &&
      linkedProductHandle === parentNode.merchandise.product?.handle &&
      Boolean(isBookBundle)
    );
  }

  if (isSubscription) {
    // For subscriptions, use the comprehensive subscription addon detection
    return isSubscriptionAddon(potentialAddon, parentNode);
  }

  return false;
}

/**
 * Determines the maximum quantity for a cart item based on product handle and quantity limitation
 * @param handle - The product handle
 * @param quantityLimitedToOne - Whether the item is quantity limited to one
 * @param parentAttributes - The parent cart item attributes to check for subscription
 * @returns The maximum quantity (1 or undefined)
 */
export function determineMaximumQuantity(
  handle: string,
  quantityLimitedToOne: string | undefined,
  parentAttributes: Array<{ key: string; value: string }> = [],
): number | undefined {
  // Array of handle patterns that should be limited to one quantity
  const quantityLimitedHandles = [
    'subscription-1',
    'course',
    'subscription-gifting',
  ];

  const isQuantityLimitedToOne = quantityLimitedToOne === 'true';
  const isHandleLimited = quantityLimitedHandles.some((pattern) =>
    handle.includes(pattern),
  );

  // Check if this is a book bundle with a subscription parent
  const isBookBundle = handle.includes('book-bundle');
  const isBookBundleWithSubscriptionParent =
    isBookBundle &&
    parentAttributes.some(
      (attr) =>
        attr.key === 'is_playgenius_subscription' && attr.value === 'true',
    );

  if (
    isHandleLimited ||
    isQuantityLimitedToOne ||
    isBookBundleWithSubscriptionParent
  ) {
    return 1;
  }

  return undefined;
}

/**
 * Maps Shopify variants to Contentful variant images
 * @param variants - Array of Shopify variants
 * @param variantImageSets - Array of Contentful variant image sets
 * @returns Array of variant objects with name and image URL
 */
function mapVariantsToImages(
  variants: Array<{ id: string; title: string; isAvailable: boolean }>,
  variantImageSets: TypeVariantImageSet[],
): Array<VariantOption> {
  if (!variants?.length || !variantImageSets?.length) {
    return [];
  }

  // Map variants to their thumbnail images
  return variants.map((variant) => {
    const variantId = variant.id.replace('gid://shopify/ProductVariant/', '');

    // Use getVariantImageSet to find the imageSet for this variant
    const imageSet = variantImageSets.find(
      (set) => set.fields?.variantId === variantId,
    );

    // Extract thumbnail URL from the imageSet
    const thumbnail = imageSet?.fields?.thumbnail;
    const imageUrl = thumbnail?.fields?.Image?.fields?.file?.url || null;

    return {
      name: variant.title,
      imageUrl,
      isAvailable: variant.isAvailable,
    };
  });
}

/**
 * Safely parses a Ruby hash string to JSON object
 * @param rubyHashString - Ruby hash formatted string
 * @param errorContext - Context for error logging
 * @returns Parsed object or null if parsing fails
 */
export function parseRubyHashToJson<T = Record<string, unknown>>(
  rubyHashString: string,
  errorContext = 'Ruby hash',
): T | null {
  try {
    const jsonString = convertRubyHashToJson(rubyHashString);
    return JSON.parse(jsonString) as T;
  } catch (e) {
    console.warn(`Failed to parse ${errorContext}:`, e);
    return null;
  }
}

/**
 * Creates a map from cart edges to product IDs for O(1) lookup
 * @param edges - Array of cart line edges
 * @returns Map of base64-encoded product IDs to cart nodes
 */
export function createProductIdToNodeMap(
  edges: { node: CartLineNode }[],
): Map<string, CartLineNode> {
  const productIdToNodeMap = new Map<string, CartLineNode>();
  edges.forEach(({ node }) => {
    const shopifyProductId = node.merchandise.product?.id;
    if (!shopifyProductId) return;

    const gidB64 = base64Encode(shopifyProductId);
    if (gidB64) {
      productIdToNodeMap.set(gidB64, node);
    }
  });
  return productIdToNodeMap;
}

/**
 * Processes raw Contentful product data into structured translation objects
 * This is the single source of truth for translation processing logic
 * Used by both server-side (direct Contentful) and client-side (API route)
 *
 * @param products - Contentful products response
 * @param pdpUrls - Contentful product pages response
 * @param productIdToNodeMap - Map of product IDs to cart nodes (for variant mapping)
 * @returns Processed translations, variants, pdpUrls, and ageRanges
 */
export function processContentfulProductData(
  products: { items?: Array<any> } | null,
  pdpUrls: { items?: Array<any> } | null,
  productIdToNodeMap: Map<string, CartLineNode>,
): {
  translations: Map<string, ExtendedCartProductTranslations>;
  variants: Map<string, Array<VariantOption>>;
  pdpUrls: Map<string, string>;
  ageRanges: Map<string, string>;
} {
  const translationsMap = new Map<string, ExtendedCartProductTranslations>();
  const variantsMap = new Map<string, Array<VariantOption>>();
  const pdpUrlsMap = new Map<string, string>();
  const ageRangesMap = new Map<string, string>();

  // Process products
  if (products?.items) {
    products.items.forEach((item) => {
      const shopifyProductIdB64 = item.fields?.[
        shopifyProductField() as keyof typeof item.fields
      ] as unknown as string | undefined;
      const productFields = item.fields as unknown as TypeProduct['fields'];
      const cartTranslations = productFields?.cartTranslations;
      const variantImageSets = productFields?.variantImageSets;
      const productTitle = productFields?.title;
      const backOrderMessage = productFields?.backOrderMessage;
      const outOfStockMessage = productFields?.outOfStockMessage;
      const ageRange = productFields?.ageRange;

      if (!shopifyProductIdB64) return;

      // Set translations
      if (cartTranslations?.fields) {
        const extendedFields = cartTranslations.fields as unknown as Record<
          string,
          unknown
        >;
        translationsMap.set(shopifyProductIdB64, {
          ...cartTranslations.fields,
          productTitle:
            cartTranslations.fields.productTitle ||
            (productTitle ? stringToRichTextDocument(productTitle) : undefined),
          backOrderMessage:
            (extendedFields.backOrderMessage as string | undefined) ||
            backOrderMessage,
          outOfStockMessage:
            (extendedFields.outOfStockMessage as string | undefined) ||
            outOfStockMessage,
        });
      } else if (productTitle) {
        translationsMap.set(shopifyProductIdB64, {
          productName: productTitle,
          productTitle: stringToRichTextDocument(productTitle),
          backOrderMessage,
          outOfStockMessage,
        });
      }

      // Set age range
      if (ageRange) {
        ageRangesMap.set(shopifyProductIdB64, ageRange);
      }

      // Process variants with node lookup
      const productNode = productIdToNodeMap.get(shopifyProductIdB64);
      if (
        productNode?.merchandise.product?.variants?.edges &&
        variantImageSets
      ) {
        const productVariants =
          productNode.merchandise.product.variants.edges.map((edge) => ({
            id: edge.node.id,
            title: edge.node.title,
            isAvailable: edge.node.availableForSale,
          }));

        if (productVariants.length > 0) {
          const mappedVariants = mapVariantsToImages(
            productVariants,
            variantImageSets,
          );
          variantsMap.set(shopifyProductIdB64, mappedVariants);
        }
      }
    });
  }

  // Process PDP URLs
  if (pdpUrls?.items) {
    pdpUrls.items.forEach((item) => {
      const fields = item.fields as unknown as TypeProductPageFields;
      const productReference = fields?.product;
      if (
        productReference?.fields?.[
          shopifyProductField() as keyof TypeProductFields
        ]
      ) {
        const shopifyProductIdB64 = productReference.fields[
          shopifyProductField() as keyof TypeProductFields
        ] as string;
        const slug = fields?.slug;

        if (shopifyProductIdB64 && slug) {
          pdpUrlsMap.set(shopifyProductIdB64, `/products/${slug}`);
        }
      }
    });
  }

  return {
    translations: translationsMap,
    variants: variantsMap,
    pdpUrls: pdpUrlsMap,
    ageRanges: ageRangesMap,
  };
}

/**
 * Module-level cache for product translations and variants
 * Stores data keyed by locale and sorted product IDs to avoid redundant API calls
 */
let productDataCache: {
  key: string;
  data: {
    translations: Map<string, ExtendedCartProductTranslations>;
    variants: Map<string, Array<VariantOption>>;
    pdpUrls: Map<string, string>;
    ageRanges: Map<string, string>;
  };
} | null = null;

/**
 * Clears the product data cache
 * Useful for testing or when you need to force a refresh
 */
export function clearProductDataCache(): void {
  productDataCache = null;
}

/**
 * Fetches product data (translations and variants) for all products in cart edges
 * Uses in-memory caching to avoid redundant API calls when product IDs haven't changed
 * @param edges - Array of cart line edges
 * @param locale - Current locale
 * @returns Object containing translations and variants maps
 */
export async function fetchProductData(
  edges: { node: CartLineNode }[],
  locale: string,
): Promise<{
  translations: Map<string, ExtendedCartProductTranslations>;
  variants: Map<string, Array<VariantOption>>;
  pdpUrls: Map<string, string>;
  ageRanges: Map<string, string>;
}> {
  const translationsMap = new Map<string, ExtendedCartProductTranslations>();
  const variantsMap = new Map<string, Array<VariantOption>>();
  const pdpUrlsMap = new Map<string, string>();
  const ageRangesMap = new Map<string, string>();
  const isClientSide = typeof window !== 'undefined';

  // Collect product IDs
  const productIds = new Set<string>();
  edges.forEach(({ node }) => {
    const shopifyProductId = node.merchandise.product?.id;
    if (!shopifyProductId) return;

    const gidB64 = base64Encode(shopifyProductId);
    if (gidB64) {
      productIds.add(gidB64);
    }
  });

  if (productIds.size === 0) {
    return {
      translations: translationsMap,
      variants: variantsMap,
      pdpUrls: pdpUrlsMap,
      ageRanges: ageRangesMap,
    };
  }

  // Create cache key from locale and sorted product IDs
  // Sorting ensures the same products in different order produce the same key
  const cacheKey = `${locale}:${Array.from(productIds).sort().join(',')}`;

  // Check if we have cached data for this exact set of products
  if (productDataCache && productDataCache.key === cacheKey) {
    return productDataCache.data;
  }

  // Client-side: Use API route to fetch raw data, then process with shared function
  // unstable_cache is a server-only Next.js API, so we call our REST endpoint
  if (isClientSide) {
    try {
      const params = new URLSearchParams({
        productIds: Array.from(productIds).join(','),
        locale,
      });

      const response = await fetch(`/api/cart/translations?${params}`);

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const data = await response.json();

      // Build lookup map for variant mapping
      const productIdToNodeMap = createProductIdToNodeMap(edges);

      // Use shared processing function - single source of truth!
      const result = processContentfulProductData(
        data.products,
        data.pdpUrls,
        productIdToNodeMap,
      );

      // Cache the result for future calls with the same products
      productDataCache = { key: cacheKey, data: result };

      return result;
    } catch (error) {
      console.error('Error fetching translations from API:', error);
      // Return empty maps on error
    }

    return {
      translations: translationsMap,
      variants: variantsMap,
      pdpUrls: pdpUrlsMap,
      ageRanges: ageRangesMap,
    };
  }

  // Server-side: Use Contentful client directly with caching
  try {
    // Build lookup map for O(1) access
    const productIdToNodeMap = createProductIdToNodeMap(edges);

    // Make parallel API calls to fetch all product data
    // Skip middleware (shouldSanitize: false) to prevent Shopify API calls in browser
    const [products, pdpUrls] = await Promise.all([
      getCartEntries<TypeProduct>({
        config: {
          contentType: CONTENT_TYPE_PRODUCT.PRODUCT,
          locale,
          [`fields.${shopifyProductField()}[in]`]:
            Array.from(productIds).join(','),
          select: PRODUCT_SELECT_FIELDS,
        },
        isPreview: false,
        shouldSanitize: false,
      }),
      getCartEntries<TypeProductPage>({
        config: {
          contentType: CONTENT_TYPE_PRODUCT_PAGE.PRODUCT_PAGE,
          locale,
          'fields.product.sys.contentType.sys.id': CONTENT_TYPE_PRODUCT.PRODUCT,
          [`fields.product.fields.${shopifyProductField()}[in]`]:
            Array.from(productIds).join(','),
          select: PRODUCT_PAGE_SELECT_FIELDS,
        },
        isPreview: false,
        shouldSanitize: false,
      }),
    ]);

    // Use shared processing function
    const result = processContentfulProductData(
      products,
      pdpUrls,
      productIdToNodeMap,
    );

    // Cache the result for future calls with the same products
    productDataCache = { key: cacheKey, data: result };

    return result;
  } catch (error) {
    console.error('Error fetching product data:', error);
    // Return empty maps if API call fails
  }

  return {
    translations: translationsMap,
    variants: variantsMap,
    pdpUrls: pdpUrlsMap,
    ageRanges: ageRangesMap,
  };
}

/**
 * Builds the cart URL based on feature flag treatment and locale
 * @param cartVariation - The feature flag value for cart treatment
 * @param currentLocale - The current locale
 * @returns The cart URL to redirect to
 */
export function buildCartUrl(
  cartVariation: FeatureFlagValues['money_enable-cart-next-js-migration'],
  currentLocale: string,
): string {
  if (cartVariation === 'treatment-page') {
    return currentLocale.startsWith('en') ? '/cart' : `/${currentLocale}/cart`;
  }

  return buildShopSubdomainUrl('/cart', currentLocale);
}

/**
 * Converts public cart item properties to private properties (underscore-prefixed)
 * This is a complete helper that handles both the conversion logic and state management
 * @param cart - The Shopify cart object
 * @param updateCartFunction - Function to update cart items
 * @param mutateFunction - SWR mutate function to update cache
 * @param setIsMutating - Function to set the mutating state
 * @param setCartError - Function to set cart error state
 * @returns Promise that resolves when the operation is complete
 */
export async function convertCartItemsToHidden(
  cart: Cart | null,
  updateCartFunction: (
    updateInputs: CartLineUpdateInput[],
    options: any,
  ) => Promise<{ ok: boolean; data?: Cart; errorMessage?: string }>,
  mutateFunction: KeyedMutator<Cart>,
  setIsMutating: (isMutating: boolean) => void,
  setCartError: (error: string | null) => void,
): Promise<void> {
  if (!cart || !shouldConvertCartItemsToHidden(cart)) {
    return;
  }

  setIsMutating(true);

  try {
    // Prepare updates for all cart line items
    const updatePromises = cart.lines.edges.map((edge) => {
      const transformedAttributes = convertPublicPropertiesToPrivateProperties(
        edge.node.attributes,
      );

      const updateInput: CartLineUpdateInput = {
        id: edge.node.id,
        quantity: edge.node.quantity,
        attributes: transformedAttributes,
      };

      return updateInput;
    });

    // Update all items at once using the provided updateCart function
    const result = await updateCartFunction(updatePromises, {});

    if (!result.ok) {
      throw new Error(
        result.errorMessage ||
          'Failed to convert cart items to hidden properties',
      );
    }

    // Update the cart data with the successful result
    if (result.data) {
      await mutateFunction(result.data, {
        rollbackOnError: true,
        revalidate: false,
      });
    }

    setCartError(null);
  } catch (error) {
    if (error instanceof Error) {
      setCartError('Failed to prepare cart for checkout. Please try again.');

      console.error('[Cart Error] Convert to hidden properties failed:', {
        error,
      });

      // Scroll to top to show error
      window?.scrollTo({ top: 0, behavior: 'smooth' });

      throw error;
    }
  } finally {
    setIsMutating(false);
  }
}

export function calculateCartTotal(cartItems: SerializedCartItem[]) {
  return cartItems.reduce((sum, item) => {
    // Sum the parent item's subtotal
    const parentSubtotal = parseFloat(item.subtotal || '0');

    // Sum all addons' subtotals
    const addonsSubtotal = item.addons.reduce((addonSum, addon) => {
      return addonSum + parseFloat(addon.subtotal || '0');
    }, 0);

    const itemTotal = parentSubtotal + addonsSubtotal;

    return sum + itemTotal;
  }, 0);
}
